import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { useToast, Toast as ToastType } from '../contexts/ToastContext';

interface ToastItemProps {
  toast: ToastType;
  onRemove: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  const [isLeaving, setIsLeaving] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      // Progress bar animation
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          const decrease = 100 / (toast.duration! / 100);
          return Math.max(0, prev - decrease);
        });
      }, 100);

      return () => clearInterval(progressInterval);
    }
  }, [toast.duration]);

  const handleRemove = () => {
    setIsLeaving(true);
    setTimeout(() => onRemove(toast.id), 300); // Match animation duration
  };

  const getToastIcon = (type: ToastType['type']) => {
    const iconClass = 'h-5 w-5 flex-shrink-0';
    switch (type) {
      case 'success':
        return <CheckCircle className={`${iconClass} text-green-600`} />;
      case 'error':
        return <AlertCircle className={`${iconClass} text-red-600`} />;
      case 'warning':
        return <AlertTriangle className={`${iconClass} text-amber-600`} />;
      case 'info':
        return <Info className={`${iconClass} text-brand-primary`} />;
      default:
        return <Info className={`${iconClass} text-gray-600`} />;
    }
  };

  const getToastStyles = (type: ToastType['type']) => {
    const baseStyles = 'border-l-4 bg-white shadow-lg rounded-lg';
    switch (type) {
      case 'success':
        return `${baseStyles} border-green-500`;
      case 'error':
        return `${baseStyles} border-red-500`;
      case 'warning':
        return `${baseStyles} border-amber-500`;
      case 'info':
        return `${baseStyles} border-brand-primary`;
      default:
        return `${baseStyles} border-gray-500`;
    }
  };

  return (
    <div
      className={`
        transform transition-all duration-300 ease-in-out mb-3
        ${isLeaving ? 'translate-x-full opacity-0 scale-95' : 'translate-x-0 opacity-100 scale-100'}
      `}
    >
      <div className={getToastStyles(toast.type)}>
        {/* Progress bar */}
        {toast.duration && toast.duration > 0 && (
          <div className="absolute top-0 left-0 h-1 bg-gray-200 rounded-t-lg w-full overflow-hidden">
            <div
              className={`h-full transition-all duration-100 ease-linear ${
                toast.type === 'success'
                  ? 'bg-green-500'
                  : toast.type === 'error'
                    ? 'bg-red-500'
                    : toast.type === 'warning'
                      ? 'bg-amber-500'
                      : 'bg-brand-primary'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        <div className="p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5">{getToastIcon(toast.type)}</div>

            <div className="ml-3 w-0 flex-1">
              <div className="text-sm font-medium text-gray-900">{toast.title}</div>
              {toast.message && (
                <div className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">
                  {toast.message}
                </div>
              )}

              {/* Action buttons */}
              {toast.actions && toast.actions.length > 0 && (
                <div className="mt-3 flex space-x-2">
                  {toast.actions.map((action, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        action.onClick();
                        handleRemove();
                      }}
                      className={`
                        text-xs font-medium px-3 py-1.5 rounded-md transition-colors
                        ${
                          action.variant === 'primary'
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }
                      `}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Close button */}
            <div className="ml-4 flex-shrink-0 flex">
              <button
                onClick={handleRemove}
                className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
              >
                <span className="sr-only">Close</span>
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useToast();
  const [portalElement, setPortalElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // Create or get the toast container
    let container = document.getElementById('toast-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'toast-container';
      container.className = `
        fixed top-4 right-4 z-50 w-80 max-w-sm pointer-events-none
        sm:w-96 sm:max-w-md
      `;
      container.style.zIndex = '9999';
      document.body.appendChild(container);
    }
    setPortalElement(container);

    return () => {
      // Cleanup when component unmounts and no toasts remain
      if (container && toasts.length === 0) {
        document.body.removeChild(container);
      }
    };
  }, [toasts.length]);

  if (!portalElement || toasts.length === 0) {
    return null;
  }

  return createPortal(
    <div className="space-y-2 pointer-events-auto">
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
      ))}
    </div>,
    portalElement
  );
};

export default ToastContainer;
